import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Search, MoreHorizontal, Pencil, Trash2, CopyPlus, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { errMsg, fmtMoney } from '../lib/format';

const EMPTY = { descripcion: '', tipo: '', condicion: '', medidas: '', precio_unitario: 0, moneda: 'MXN', proveedor: '', unidad: 'pza' };

export default function CatalogoContenedoresPage() {
  const queryClient = useQueryClient();
  const { canDelete } = useAuth();
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['catalog', 'containers', search],
    queryFn: async () => (await api.get('/catalog/containers', { params: { search: search || undefined, limit: 200 } })).data,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['catalog'] });
  const onError = (e) => toast.error(errMsg(e));

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editId) return (await api.put(`/catalog/containers/${editId}`, form)).data;
      return (await api.post('/catalog/containers', form)).data;
    },
    onSuccess: () => {
      invalidate();
      toast.success(editId ? 'Contenedor actualizado (espejo CONT- sincronizado)' : 'Contenedor registrado y espejado en Catálogo Prefab');
      setDialogOpen(false);
    },
    onError,
  });

  const saveAsNewMutation = useMutation({
    mutationFn: async (id) => (await api.post(`/catalog/containers/${id}/save-as-new`, {})).data,
    onSuccess: (item) => { invalidate(); toast.success(`Guardado como ${item.codigo}`); },
    onError,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => (await api.delete(`/catalog/containers/${id}`)).data,
    onSuccess: () => { invalidate(); toast.success('Contenedor eliminado'); },
    onError,
  });

  const sendToPrefabMutation = useMutation({
    mutationFn: async (id) => (await api.post('/catalog/send-to-prefab', { catalog_type: 'containers', item_id: id })).data,
    onSuccess: (r) => toast.success(r.mensaje || 'Espejado en catálogo Prefab'),
    onError,
  });

  const openNew = () => { setForm(EMPTY); setEditId(null); setDialogOpen(true); };
  const openEdit = (item) => { setForm({ ...EMPTY, ...item }); setEditId(item.id); setDialogOpen(true); };
  const items = data?.items || [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Catálogo de Contenedores</h2>
          <p className="text-sm text-muted-foreground">Los contenedores se espejan automáticamente en el Catálogo Prefab como CONT-</p>
        </div>
        <Button onClick={openNew} data-testid="new-container-catalog-button">
          <Plus size={16} className="mr-1.5" /> Nuevo contenedor
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar contenedor..." className="pl-9" data-testid="container-catalog-search-input" />
      </div>

      <Card className="border-border shadow-[var(--shadow-elev-1)]">
        <CardContent className="p-0">
          {isLoading ? <div className="p-6 space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-10" />)}</div> : (
            <Table data-testid="container-catalog-table">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="th-label">Código</TableHead>
                  <TableHead className="th-label">Descripción</TableHead>
                  <TableHead className="th-label">Tipo</TableHead>
                  <TableHead className="th-label">Condición</TableHead>
                  <TableHead className="th-label">Medidas</TableHead>
                  <TableHead className="th-label text-right">Precio</TableHead>
                  <TableHead className="th-label w-14"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 && (
                  <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Sin contenedores registrados</TableCell></TableRow>
                )}
                {items.map((item) => (
                  <TableRow key={item.id} className="hover:bg-background/40" data-testid={`container-catalog-row-${item.codigo}`}>
                    <TableCell className="font-mono text-[13px]">{item.codigo}</TableCell>
                    <TableCell className="font-medium max-w-[240px] truncate">{item.descripcion}</TableCell>
                    <TableCell className="text-muted-foreground">{item.tipo || '—'}</TableCell>
                    <TableCell className="text-muted-foreground">{item.condicion || '—'}</TableCell>
                    <TableCell className="text-muted-foreground text-[13px]">{item.medidas || '—'}</TableCell>
                    <TableCell className="money">{fmtMoney(item.precio_unitario, item.moneda)}</TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`container-catalog-actions-${item.codigo}`}>
                            <MoreHorizontal size={15} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(item)}><Pencil size={14} className="mr-2" /> Editar</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => saveAsNewMutation.mutate(item.id)}><CopyPlus size={14} className="mr-2" /> Guardar como nuevo (-V2)</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => sendToPrefabMutation.mutate(item.id)} data-testid="container-action-send-prefab">
                            <Send size={14} className="mr-2" /> Enviar a Catálogo Prefab
                          </DropdownMenuItem>
                          {canDelete && (
                            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => deleteMutation.mutate(item.id)}>
                              <Trash2 size={14} className="mr-2" /> Eliminar
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="container-catalog-dialog">
          <DialogHeader><DialogTitle>{editId ? `Editar ${form.codigo}` : 'Nuevo contenedor'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Descripción *</Label>
              <Input value={form.descripcion} onChange={(e) => setForm((f) => ({ ...f, descripcion: e.target.value }))} data-testid="container-descripcion-input" />
            </div>
            <div className="space-y-1.5"><Label>Tipo</Label><Input value={form.tipo} onChange={(e) => setForm((f) => ({ ...f, tipo: e.target.value }))} placeholder="20ft / 40ft HC" data-testid="container-tipo-input" /></div>
            <div className="space-y-1.5"><Label>Condición</Label><Input value={form.condicion} onChange={(e) => setForm((f) => ({ ...f, condicion: e.target.value }))} placeholder="nuevo / usado" data-testid="container-condicion-input" /></div>
            <div className="col-span-2 space-y-1.5"><Label>Medidas</Label><Input value={form.medidas} onChange={(e) => setForm((f) => ({ ...f, medidas: e.target.value }))} placeholder="largo x ancho x alto" data-testid="container-medidas-input" /></div>
            <div className="space-y-1.5"><Label>Precio unitario</Label><Input type="number" step="0.01" value={form.precio_unitario} onChange={(e) => setForm((f) => ({ ...f, precio_unitario: Number(e.target.value) }))} data-testid="container-precio-input" /></div>
            <div className="space-y-1.5">
              <Label>Moneda</Label>
              <Select value={form.moneda} onValueChange={(v) => setForm((f) => ({ ...f, moneda: v }))}>
                <SelectTrigger data-testid="container-moneda-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="MXN">MXN</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5"><Label>Proveedor</Label><Input value={form.proveedor} onChange={(e) => setForm((f) => ({ ...f, proveedor: e.target.value }))} data-testid="container-proveedor-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={!form.descripcion || saveMutation.isPending} data-testid="save-container-catalog-button">
              {editId ? 'Guardar cambios' : 'Registrar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
